/**
 * Lifecycle of a validation-asset generation request (the `validation_asset_request`
 * row status). `requested` on creation → `rendering` when the pdf-generator-worker
 * picks it up → `ready` on success (with a `resultManifest`) or `failed` (with an
 * `error`). Stored as text; use this enum, never a literal.
 */
export enum ValidationAssetStatus {
	REQUESTED = 'requested',
	RENDERING = 'rendering',
	READY = 'ready',
	FAILED = 'failed',
}

/**
 * The kind of a produced validation-asset output — the short slug the worker
 * records alongside each file in the `resultManifest`. `png` is a visual mock,
 * `pdf` is the verification document. The human-facing display name is the
 * separate `label` (owned by the template), NOT this slug.
 */
export enum ValidationAssetOutputKind {
	PNG = 'png',
	PDF = 'pdf',
}

/**
 * The validation lane a work order is in, for the transition engine's guard
 * vocabulary and the review-queue UI. Never persisted: it is derived from the
 * work order's configured status code (see `resolveLane`), so it cannot drift
 * out of sync with the status. A workflow whose validation statuses are named
 * something else simply has no lane, which is a legitimate configuration.
 */
export enum ValidationLane {
	INTERNAL = 'internal',
	DESIGN = 'design',
	COMPLIANCE = 'compliance',
	/** The designer's check of the order's source material, before production. */
	SOURCE_MATERIAL = 'source_material',
}

/**
 * A reviewer's mark on one deliverable, on one review stage. A tile's current
 * mark is its newest row; `CLEARED` is an un-mark, recorded rather than deleted
 * so the history keeps it.
 *
 * Stored as text with no CHECK constraint, and validated in the backend (the
 * HTTP schema and `DeliverableReview.fromRequest`), so a new verdict is a new
 * member here and needs no migration. Not a boolean: each row records an action,
 * and a third one would otherwise mean a column type change and a rewrite of
 * the append-only history.
 */
export enum ReviewVerdict {
	REVIEWED = 'reviewed',
	CLEARED = 'cleared',
}

/** Where a work order originated. */
export enum WorkOrderSource {
	WORKFRONT = 'workfront',
	MANUAL = 'manual',
}

/**
 * The level of a work order. Only the Workfront sync creates a factory brief, the
 * campaign-level request; every manual order, and every order under a brief, is an
 * asset brief, so a manual order without a parent is an asset brief too.
 */
export enum WorkOrderKind {
	FACTORY_BRIEF = 'factory_brief',
	ASSET_BRIEF = 'asset_brief',
}

/**
 * Effort tier (taxonomy sheet 6). Distinct from {@link AssetTypeCode} — this is
 * "how much work", not "what piece". Stored as the `complexity_level` attribute
 * on the work-order anchor, never as a column.
 */
export enum ComplexityLevel {
	L1 = 'L1',
	L2 = 'L2',
	L3 = 'L3',
	L4 = 'L4',
}

/**
 * The piece being produced — full catalog. Distinct from
 * {@link ComplexityLevel}. Stored as the `asset_type_code` attribute on the
 * work-order anchor. The combined display code (e.g. `L4` + `E3` → `L4E3`) is
 * derived for presentation only and never stored.
 */
export enum AssetTypeCode {
	// P — Print
	P1 = 'P1', // Flyer
	P2 = 'P2', // Leaflet / leave-behind
	P3 = 'P3', // Poster
	P4 = 'P4', // Brochure bi/tri-fold
	P5 = 'P5', // Brochure extended
	P6 = 'P6', // Roll-up
	P7 = 'P7', // Congress panel / backdrop
	P8 = 'P8', // Counter card / wobbler / shelf talker
	// D — Digital · Social & Display
	D1 = 'D1', // Social single static feed post
	D2 = 'D2', // Social static story / reel
	D3 = 'D3', // Carousel static feed (3–5 frames)
	D4 = 'D4', // Carousel static story (3–5 frames)
	D5 = 'D5', // Static feed ×3 placements (FB/IG/LI)
	D6 = 'D6', // Single video feed post
	D7 = 'D7', // Video story / reel
	D8 = 'D8', // UGC / influencer video edit
	D9 = 'D9', // Video cutdown
	D10 = 'D10', // YouTube pre-roll adaptation
	D11 = 'D11', // Static display banner
	D12 = 'D12', // Video display banner
	D13 = 'D13', // SEM ad copy set
	// E — Digital · Email
	E1 = 'E1', // Email (short)
	E2 = 'E2', // Email (long) / Newsletter
	E3 = 'E3', // Email master template
	// W — Digital · Website
	W1 = 'W1', // Landing page (long)
	W2 = 'W2', // Landing page (short)
	W3 = 'W3', // Website module
	W4 = 'W4', // Landing page (article)
	W5 = 'W5', // Push / in-app static message
	// DO — Digital · Others
	DO1 = 'DO1', // Scientific infographic (digital static)
	DO2 = 'DO2', // Waiting-room screen static graphic
	// DS — Digital · Sales Materials
	DS1 = 'DS1', // Sales aid / objection handler
	DS2 = 'DS2', // IVA/CLM short presentation (≤5 slides)
	DS3 = 'DS3', // IVA/CLM extended presentation (≤10 slides)
	// V — Video Services
	V1 = 'V1', // Short-form video edit (≤30s)
	V2 = 'V2', // Animated infographic / explainer (≤90s)
	V3 = 'V3', // Product / MOA video edit (≤3min)
	V4 = 'V4', // Video resizes
	V5 = 'V5', // Video captions
	V6 = 'V6', // Video subtitling
}

/** The `sort` keys `GET /work-orders` accepts. */
export enum WorkOrderSortKey {
	ORDER_CODE = 'orderCode',
	TITLE = 'title',
	/** The brand label of the order's brand-axis classification. */
	BRAND = 'brand',
	/** The status's configured position in the order's workflow, not its code. */
	STATUS = 'status',
	/** The display name of the order's alphabetically first assignee. */
	ASSIGNEE = 'assignee',
	DUE_AT = 'dueAt',
	CREATED_AT = 'createdAt',
	UPDATED_AT = 'updatedAt',
	STATUS_CHANGED_AT = 'statusChangedAt',
}
